import {
  AttentionBlock,
  Button,
  FileButton,
  ListRow,
  ListRows,
  PhotoGrid,
  PhotoTile,
  QAGroup,
  QAPair,
  StatusTag,
  formatCurrency,
  titleCase,
  toast,
} from '@westhetics/ui'
import { useState } from 'react'
import { MobileActionBar } from '@/features/patients/mobile-action'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { useUploadStagePhoto, type Stage, type TreatmentPlan } from './api'
import {
  CONTINUE_ON_DESKTOP,
  IMAGE_CONSENT_LABELS,
  STAGES,
  STAGE_LABELS,
  joinProcedures,
  photoCaption,
  photoCount,
  productName,
  recordedStagesText,
} from './labels'
import { PHOTO_ACCEPT } from './photo-file'
import { STAGE_LIMIT } from './photos-tab'
import { usePhotoCapture } from './use-photo-capture'

/**
 * O plano abaixo de 900px (RN-PLA-35; docs/areas/mobile.md): leitura, com as fotos de cada etapa
 * em cartões "Ver" ou "Fotografar" e o botão fixo "Fotografar · Marcação" ao alcance do polegar.
 * Produto, regiões, lote em texto e encerramento continuam no computador. A foto sobe pelo
 * próprio celular logado (`source` conforme a captura), sem QR.
 */
export function PlanMobile({
  plan,
  readOnly,
  actions,
}: {
  plan: TreatmentPlan
  readOnly: boolean
  /** O que cabe no celular além das fotos (baixar o PDF de um plano encerrado). */
  actions?: React.ReactNode
}) {
  const upload = useUploadStagePhoto(plan.id)
  const capture = usePhotoCapture()
  const [open, setOpen] = useState<Set<Stage>>(() => new Set())
  const [busy, setBusy] = useState<Stage | null>(null)

  async function shoot(stage: Stage, files: File[], source: 'desktop' | 'gallery') {
    if ((plan.photos[stage]?.length ?? 0) >= STAGE_LIMIT) {
      toast(
        `Esta etapa já tem ${STAGE_LIMIT} fotos, o máximo. Arquive uma no computador para adicionar outra.`,
      )
      return
    }
    await capture(
      files.slice(0, 1),
      async (photo) => {
        setBusy(stage)
        try {
          await upload.mutateAsync({ stage, photo, source })
          setOpen((current) => new Set(current).add(stage))
          toast(`Foto registrada em ${STAGE_LABELS[stage].title}.`)
        } catch (error) {
          const fields = fieldErrors(error)
          toast(fields.file ?? errorMessage(error))
        } finally {
          setBusy(null)
        }
      },
      (message) => toast(message),
    )
  }

  const nextStage = STAGES.find((stage) => (plan.photos[stage]?.length ?? 0) === 0) ?? 'after'

  return (
    <div className="flex flex-col gap-6">
      <QAGroup title="Atendimento">
        <QAPair
          question="Procedimentos"
          answer={joinProcedures(plan.procedures.map((p) => p.name))}
        />
        <QAPair question="Profissional" answer={titleCase(plan.practitioner.name)} />
        <QAPair question="Valor" answer={formatCurrency(plan.totalAmountCents / 100)} />
        <QAPair question="Uso de imagem" answer={IMAGE_CONSENT_LABELS[plan.imageConsent]} />
      </QAGroup>
      {plan.notes ? (
        <section aria-label="Observações">
          <h2 className="eyebrow">Observações</h2>
          <p className="font-body text-body text-ink mt-2 font-light whitespace-pre-line">
            {plan.notes}
          </p>
        </section>
      ) : null}

      <section aria-label="Fotos por etapa" className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="eyebrow">Fotos do tratamento</h2>
          <span className="font-body text-eyebrow text-label font-light tracking-normal">
            {recordedStagesText(plan.recordedStageCount)}
          </span>
        </div>
        <ListRows>
          {STAGES.map((stage) => {
            const photos = plan.photos[stage] ?? []
            const label = STAGE_LABELS[stage]
            const expanded = open.has(stage)
            return (
              <div key={stage}>
                <ListRow
                  size="name"
                  title={label.title}
                  meta={`${label.description} · ${photoCount(photos.length)}`}
                  trailing={
                    <div className="flex flex-col items-end gap-2">
                      {photos.length === 0 && plan.status === 'open' ? (
                        <StatusTag level="attention">nenhuma foto</StatusTag>
                      ) : null}
                      <div className="flex items-center gap-3">
                        {photos.length > 0 ? (
                          <Button
                            variant="text"
                            size="sm"
                            onClick={() =>
                              setOpen((c) => {
                                const next = new Set(c)
                                if (next.has(stage)) next.delete(stage)
                                else next.add(stage)
                                return next
                              })
                            }
                          >
                            {expanded ? 'Fechar' : 'Ver'}
                          </Button>
                        ) : null}
                        {!readOnly ? (
                          <FileButton
                            variant="text"
                            size="sm"
                            accept={PHOTO_ACCEPT}
                            capture="environment"
                            loading={busy === stage}
                            loadingLabel="Enviando…"
                            onFiles={(files) => void shoot(stage, files, 'desktop')}
                          >
                            Fotografar
                          </FileButton>
                        ) : null}
                      </div>
                    </div>
                  }
                />
                {expanded && photos.length > 0 ? (
                  <div className="border-hairline border-b px-4 py-3 last:border-b-0">
                    <PhotoGrid>
                      {photos.map((photo) => (
                        <PhotoTile
                          key={photo.id}
                          src={photo.url}
                          href={photo.url ?? undefined}
                          alt={`Foto ${photo.position ?? ''} da etapa ${label.title}`}
                          caption={photoCaption(photo)}
                        />
                      ))}
                    </PhotoGrid>
                  </div>
                ) : null}
              </div>
            )
          })}
        </ListRows>
      </section>

      <section aria-label="Produtos" className="flex flex-col gap-3">
        <h2 className="eyebrow">Produtos · {plan.products.length}</h2>
        {plan.products.length === 0 ? (
          <p className="font-body text-secondary text-[13px] leading-[1.6] font-light">
            Nenhum produto registrado.
          </p>
        ) : (
          <ListRows>
            {plan.products.map((product) => (
              <ListRow
                key={product.id}
                size="lead"
                title={productName(product)}
                meta={[
                  product.manufacturer,
                  product.lotNumber ? `lote ${product.lotNumber}` : null,
                  product.regions.map((r) => r.label).join(', ') || null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                trailing={
                  product.isLotPending && plan.status === 'open' ? (
                    <StatusTag level="attention">lote pendente</StatusTag>
                  ) : undefined
                }
              />
            ))}
          </ListRows>
        )}
        {plan.status === 'open' && !readOnly ? (
          <AttentionBlock variant="lilac">
            Produto, regiões, lote e o encerramento do plano continuam no computador (
            {CONTINUE_ON_DESKTOP}).
          </AttentionBlock>
        ) : null}
      </section>

      {actions}

      {!readOnly ? (
        <MobileActionBar>
          <FileButton
            size="lg"
            accept={PHOTO_ACCEPT}
            capture="environment"
            loading={busy !== null}
            loadingLabel="Enviando…"
            onFiles={(files) => void shoot(nextStage, files, 'desktop')}
          >
            Fotografar · {STAGE_LABELS[nextStage].title}
          </FileButton>
        </MobileActionBar>
      ) : null}
    </div>
  )
}
